<template>
  <div class="flex flex-col gap-5">
    <div class="flex items-center gap-2 cursor-pointer"
      :class="{ 'opacity-50 pointer-events-none': isLoading || isRequesting }" @click="callLocationPermission">
      <UIcon name="i-lucide-locate-fixed" class="text-primary w-5 h-5" />
      <span class="text-[#0C5542] cta-link text-ellipsis font-medium">
        Use my current location
      </span>
    </div>

    <div class="text-red-500 text-sm" v-if="error">{{ error }}</div>
  </div>
</template>


<script setup lang="ts">
import { GoogleAnalytics } from '~/google_analytics/events'
import { EventParameter } from '~/google_analytics/events.enum'
import { useGeoLocation } from '~/stores/geolocation'

const { $callPermissionFromBFLApp } = useNuxtApp();
const { getCurrentLocation, isLoading, emitter } = useLocationFinder()
const checkout = useCheckout()
const geolocation = useGeoLocation()
const { gtag } = useGtag()
const auth = useAuth()
const LOCATION_PERMISSION_STATUS = "location_permission_status"
const isAuthenticated = !!auth?.access_token
const userData = isAuthenticated
  ? {
    user_name: auth.user.first_name,
    user_phone: auth.user.phone,
    user_email: auth.user.email,
  }
  : {
    user_name: null,
    user_phone: null,
    user_email: null,
  }

const error = ref<string | null>(null)
const isRequesting = ref(false)

const gps = computed(() => geolocation.selectedAddress?.gps);

// Derived values
const hasLocation = computed(() => !!(gps.value?.latitude && gps.value?.longitude));
const latitude = computed(() => gps.value?.latitude || '0');
const longitude = computed(() => gps.value?.longitude || '0');
const selectedAddress = computed(() => geolocation.formatSelectedAddress)

const emits = defineEmits<{
  (e: 'success'): void
  (e: 'error', message: string): void
}>()

const onSuccess = async (coords: { latitude: number; longitude: number }) => {

  if (!isRequesting.value) return
  isRequesting.value = false
  error.value = null
  await geolocation.setLocationByCoordinates(coords.latitude, coords.longitude)

  emits('success')
}

const onError = (message: string) => {
  error.value = message
  emits('error', message)
}

onUnmounted(() => {
  emitter.off('success', onSuccess as any)
  emitter.off('error', onError as any)
})



onMounted(() => {
  emitter.on('success', onSuccess as any)
  emitter.on('error', onError as any)
})

const callLocationPermission = async () => {
  if (isLoading.value || isRequesting.value) return;

  isRequesting.value = true;
  error.value = null;
  checkout.delivery_address = null;

  const previousPermission =
    localStorage.getItem(LOCATION_PERMISSION_STATUS) || "never_asked";

  // 🔹 helper to build analytics payload
  const buildPayload = (action: string, reason: string) => ({
    [EventParameter.ACTION]: action,
    [EventParameter.LATITUDE]: latitude.value,
    [EventParameter.LONGITUDE]: longitude.value,
    [EventParameter.ADDRESS]: selectedAddress.value || "none",
    [EventParameter.REASON]: reason,
    [EventParameter.LOCATION_AVAILABLE]: hasLocation.value ? "yes" : "no",
    [EventParameter.PROMPT_TYPE]: "none",
    [EventParameter.NEEDS_LOCATION_FETCH]: hasLocation.value ? "no" : "yes",
  });

  // 🔹 Track user intent click
  GoogleAnalytics.locationEvent(
    buildPayload("use_current_location", "user_initiated")
  );

  try {
    const result = await $callPermissionFromBFLApp("location");

    if (result?.status !== "success") {
      throw new Error("PERMISSION_DENIED");
    }

    // 🔹 Determine reason
    const reason =
      previousPermission === "never_asked"
        ? "user_granted_now"
        : "user_allow_previous";

    // 🔹 Persist
    localStorage.setItem(LOCATION_PERMISSION_STATUS, "granted");

    // 🔹 Analytics
    GoogleAnalytics.locationEvent(
      buildPayload("location_permission_granted", reason)
    );

    // 🔹 Fetch location
    await getCurrentLocation();
  } catch (err: any) {
    const isDenied = err?.message === "PERMISSION_DENIED";

    // 🔹 Determine reason
    const reason =
      previousPermission === "never_asked"
        ? "user_denied_now"
        : "user_denied_previous";

    // 🔹 Persist
    localStorage.setItem(LOCATION_PERMISSION_STATUS, "denied");

    if (isDenied) {
      GoogleAnalytics.locationEvent(
        buildPayload("location_permission_denied", reason)
      );

      error.value =
        "Permission denied. Go to App Settings (or) Browser Settings > Permissions > Enable Location Access.";
    } else {
      error.value =
        err?.message ||
        "Unable to get your location. Please ensure GPS/location is enabled and try again.";
    }
  } finally {
    isRequesting.value = false;
  }
};
</script>
